import React, { forwardRef } from "react"
import { motion } from "framer-motion"
import { ChevronDown } from "lucide-react"

const baseInput =
  "w-full h-[44px] rounded-input border border-app-content-faint/30 bg-surface-main " +
  "text-app-content placeholder-app-content-faint outline-none transition-colors " +
  "focus:border-brand-main focus:ring-2 focus:ring-brand-main/20 " +
  "disabled:opacity-disabled disabled:cursor-not-allowed"

const errorInput = "border-red-400 focus:border-red-500 focus:ring-red-200"

export const SectionCard = ({ icon: Icon, title, children, index = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: index * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="bg-surface-main rounded-card shadow-sm border border-app-content-faint/10 p-4 sm:p-6"
  >
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon className="w-6 h-6 text-brand-main shrink-0" />}
      <h2 className="text-lg font-bold text-app-base-strong">{title}</h2>
    </div>
    {children}
  </motion.section>
)

export const Field = ({ label, action, error, hint, className = "", children }) => (
  <div className={`flex flex-col gap-1.5 min-w-0 ${className}`}>
    {(label || action) && (
      <div className="flex items-end justify-between gap-2 min-h-[20px]">
        {label && <label className="text-sm text-text-secondary">{label}</label>}
        {action}
      </div>
    )}
    {children}
    {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
)

export const TextInput = forwardRef(
  ({ align = "left", invalid, className = "", ...props }, ref) => (
    <input
      ref={ref}
      type="text"
      {...props}
      className={`${baseInput} px-4 ${align === "right" ? "text-right" : ""} ${
        invalid ? errorInput : ""
      } ${className}`}
    />
  )
)

export const TextArea = ({ invalid, rows = 4, className = "", ...props }) => (
  <textarea
    rows={rows}
    {...props}
    className={`w-full px-4 py-3 rounded-input border border-app-content-faint/30 bg-surface-main text-app-content placeholder-app-content-faint outline-none transition-colors focus:border-brand-main focus:ring-2 focus:ring-brand-main/20 resize-y ${
      invalid ? errorInput : ""
    } ${className}`}
  />
)

export const NumberInput = ({ value, onChange, ...props }) => (
  <TextInput
    inputMode="decimal"
    align="right"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value.replace(",", ".").replace(/[^0-9.]/g, ""))}
    {...props}
  />
)

export const SelectInput = ({
  options = [],
  value,
  onChange,
  placeholder = "",
  icon: Icon,
  invalid,
  disabled,
  valueKey = "id",
  labelKey = "name",
}) => (
  <div className="relative">
    {Icon && (
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-app-base-strong pointer-events-none" />
    )}
    <select
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${baseInput} appearance-none pr-10 ${Icon ? "pl-11" : "pl-4"} ${
        invalid ? errorInput : ""
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o[valueKey]} value={o[valueKey]}>
          {o[labelKey]}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-base-strong pointer-events-none" />
  </div>
)

export const DateInput = ({ value, onChange, invalid }) => (
  <input
    type="date"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className={`${baseInput} px-4 ${invalid ? errorInput : ""}`}
  />
)

export const Toggle = ({ checked, onChange, label, hint }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-3 text-left"
  >
    <span
      className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
        checked ? "bg-brand-main" : "bg-app-content-faint/40"
      }`}
    >
      <motion.span
        layout
        transition={{ type: "spring", stiffness: 500, damping: 32 }}
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow ${
          checked ? "right-0.5" : "left-0.5"
        }`}
      />
    </span>
    <span className="flex flex-col">
      <span className="text-app-content font-medium">{label}</span>
      {hint && <span className="text-xs text-text-muted">{hint}</span>}
    </span>
  </button>
)
